import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
// Global Styled Components
import Spacer from '../styles/Spacer'
import Icon from '../styles/Icon'
import { ThreeColumn, FourColumn } from '../styles/Columns'
import { Paragraph } from '../styles/Text'
import TextLink from '../../components/TextLink'
import { SMARTY_PRICE_GUIDE } from '../../global.constants'
import { theme } from '../../constants/theme'

// Images
import FiveGReady from '../../static/images/5G-ready.svg'
import EURoaming from '../../static/images/EU-roaming.svg'
import NoAnnualPriceRise from '../../static/images/no-annual-price-rise.svg'
import NoContract from '../../static/images/noContracts.svg'
import Addons from '../../static/images/addons.svg'
import Unlimited from '../../static/images/unlimited.svg'
import Checkmark from '../../static/images/checkmark.svg'

import { WhyCard, WhyCardTitle, WhyTitle } from './styles'

const WhySmarty = ({ darkTheme }) => (
  <section>
    <Spacer />
    <WhyTitle id="why-smarty" marginBottom="8px" data-testid="why-smarty-title">
      Why choose SMARTY?
    </WhyTitle>
    <Paragraph margin="0 0 24px" data-testid="why-smarty-subtitle">
      Let’s keep it simple
    </Paragraph>
    <FourColumn>
      <WhyCard data-testid="commitment-free-SIM-card">
        <Icon
          src={NoContract}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="commitment-free-SIM-icon"
        />
        <WhyCardTitle data-testid="commitment-free-SIM-title">
          Commitment- free SIM plans
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="commitment-free-SIM-text"
        >
          1 month plans. No contracts, no credit checks and no surprises at the
          end of the month. Cancel or change plans anytime. Pay in advance and
          enjoy the flexibility.
        </Paragraph>
      </WhyCard>
      <WhyCard data-testid="add-ons-card">
        <Icon
          src={Addons}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="add-ons-icon"
        />
        <WhyCardTitle data-testid="add-ons-title">
          Add-ons that never expire
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="add-ons-text"
        >
          If you run out of data, add-ons are £1 per GB - the same as it costs
          on our Data discount plans. Buy them from your dashboard and keep them
          until you use every MB.
        </Paragraph>
      </WhyCard>
      <WhyCard data-testid="truly-unlimited-card">
        <Icon
          src={Unlimited}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="truly-unlimited-icon"
        />
        <WhyCardTitle data-testid="truly-unlimited-title">
          Truly unlimited calls &amp; texts
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="truly-unlimited-text"
        >
          To standard UK landlines and mobiles, and roaming within EU countries.
          Call and text your friends and family as much as you want. No catch.
        </Paragraph>
      </WhyCard>
      <WhyCard data-testid="keep-your-number-card">
        <Icon
          src={Checkmark}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="keep-your-number-icon"
        />
        <WhyCardTitle data-testid="keep-your-number-title">
          Keep your number
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="keep-your-number-text"
        >
          Simply text ‘PAC’ to 65075 to get a code valid for 30 days. We’ll walk
          you through steps to enter this code to transfer the phone number you
          want to keep.
        </Paragraph>
      </WhyCard>
    </FourColumn>
    <Spacer size={1.65} />
  </section>
)

WhySmarty.propTypes = {
  darkTheme: PropTypes.bool,
}

const ReasonsToLoveSmarty = ({ darkTheme }) => (
  <section>
    <Spacer />
    <WhyTitle id="why-smarty" marginBottom="8px" data-testid="why-smarty-title">
      Reasons to Love SMARTY?
    </WhyTitle>
    <Paragraph margin="0 0 24px" data-testid="why-smarty-subtitle">
      Let’s keep it simple
    </Paragraph>
    <ThreeColumn>
      <WhyCard data-testid="five-g-ready-card">
        <Icon
          src={FiveGReady}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="five-g-ready-icon"
        />
        <WhyCardTitle data-testid="five-g-ready-title">5G ready</WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="five-g-ready-text"
        >
          Stream, play and browse with more reliability and superfast 5G at no
          extra cost.&nbsp;
          <TextLink dataTestId="5g-more" to="5g">
            Find out more
          </TextLink>
          <br />
          <br />
          5G subject to{' '}
          <TextLink dataTestId="5g-coverage-checker" to="coverage-checker">
            coverage
          </TextLink>
          , compatible device required.
        </Paragraph>
      </WhyCard>
      <WhyCard data-testid="eu-roaming-still-included-card">
        <Icon
          src={EURoaming}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="eu-roaming-still-included-icon"
        />
        <WhyCardTitle data-testid="eu-roaming-still-included-title">
          EU roaming still included
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="eu-roaming-still-included-text"
        >
          With SMARTY you can continue to use your allowance in European
          destinations to make calls and send texts to the UK and you can use up
          to 12GB of data to get online.
          <br />
          <Link
            data-testid="eu-roaming-check-pricing"
            target="_blank"
            to={SMARTY_PRICE_GUIDE}
          >
            Check pricing
          </Link>
        </Paragraph>
      </WhyCard>
      <WhyCard data-testid="no-annual-price-rise-card">
        <Icon
          src={NoAnnualPriceRise}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="no-annual-price-rise-icon"
        />
        <WhyCardTitle data-testid="no-annual-price-rise-title">
          No annual price rise
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="no-annual-price-rise-text"
        >
          We won’t force annual price increases on you, you can make the most of
          simple, honest mobile, confident that your bill will be just what you
          expect with our flexible monthly plans.
        </Paragraph>
      </WhyCard>
    </ThreeColumn>
    <Spacer size={1.65} />
  </section>
)

ReasonsToLoveSmarty.propTypes = {
  darkTheme: PropTypes.bool,
}

const WhySmartyPlans = ({ darkTheme }) => (
  <section>
    <ThreeColumn>
      <WhyCard data-testid="one-month-plan-card">
        <Icon
          src={NoContract}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="one-month-plan-icon"
        />
        <WhyCardTitle data-testid="one-month-plan-title">
          1 month plans
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="one-month-plan-text"
        >
          Cancel or change plans anytime. No contracts, no credit checks and no
          surprises at the end of the month. Pay in advance and enjoy the
          flexibility.
        </Paragraph>
      </WhyCard>
      <WhyCard data-testid="keep-your-number-card">
        <Icon
          src={Checkmark}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="keep-your-number-icon"
        />
        <WhyCardTitle data-testid="keep-your-number-title">
          Keep your number
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="keep-your-number-text"
        >
          Simply text ‘PAC’ to 65075 to get a code valid for 30 days. We’ll walk
          you through steps to enter this code to transfer the phone number you
          want to keep.
        </Paragraph>
      </WhyCard>
      <WhyCard data-testid="tether-without-worring-card">
        <Icon
          src={Addons}
          alt=""
          width="64px"
          height="64px"
          margin="0 0 30px"
          data-testid="tether-without-worring-icon"
        />
        <WhyCardTitle data-testid="tether-without-worring-title">
          Tether without worrying
        </WhyCardTitle>
        <Paragraph
          color={darkTheme && theme.colors.fonts.lightGrey}
          data-testid="tether-without-worring-text"
        >
          You can tether and hot-spot all of your data with no speed caps as
          long as you’re in the UK.
        </Paragraph>
      </WhyCard>
    </ThreeColumn>
  </section>
)

WhySmartyPlans.propTypes = {
  darkTheme: PropTypes.bool,
}

const WhySmartyUnlimited = ({ darkTheme }) => (
  <section>
    <Spacer />
    <WhyTitle id="why-smarty" marginBottom="32px">
      Why choose SMARTY?
    </WhyTitle>
    <ThreeColumn>
      <div>
        <Icon
          src={Checkmark}
          alt=""
          width="90px"
          height="90px"
          margin="0 0 30px"
        />
        <WhyCardTitle>SIM only, unlimited data</WhyCardTitle>
        <Paragraph color={darkTheme && theme.colors.fonts.grey}>
          No contracts, no credit checks and no surprises at the end of the
          month with our Unlimited SIMs.
        </Paragraph>
      </div>
      <div>
        <Icon
          src={Addons}
          alt=""
          width="90px"
          height="90px"
          margin="0 0 30px"
        />
        <WhyCardTitle>Unlimited data plan, no speed restrictions</WhyCardTitle>
        <Paragraph color={darkTheme && theme.colors.fonts.grey}>
          All the internet you need without worrying about hefty bills and data
          speed restrictions. You can tether it too!
        </Paragraph>
      </div>
      <div>
        <Icon
          src={Unlimited}
          alt=""
          width="90px"
          height="90px"
          margin="0 0 30px"
        />
        <WhyCardTitle>Unlimited UK calls and texts – on all plans</WhyCardTitle>
        <Paragraph color={darkTheme && theme.colors.fonts.grey}>
          Call your friends and family as much as you want. No catch.
        </Paragraph>
      </div>
    </ThreeColumn>
    <Spacer />
  </section>
)

WhySmartyUnlimited.propTypes = {
  darkTheme: PropTypes.bool,
}

export { WhySmarty, ReasonsToLoveSmarty, WhySmartyPlans, WhySmartyUnlimited }
